import type { AxiosRequestConfig } from 'axios';
import type {
  ListMarketAuditLogsReply,
  ListMarketCategoriesReply,
  ListMarketItemsReply,
  ListMarketReleasesReply,
  MarketApplicationTarget,
  MarketCategory,
  MarketItem,
  MarketItemType,
  MarketRelease,
  MarketType,
  ProtoInt64,
} from '@/types';

interface MarketHttpClient {
  post<T>(
    path: string,
    body: unknown,
    options?: AxiosRequestConfig,
  ): Promise<T>;
}
export interface MarketRequestOptions {
  readonly signal?: AbortSignal;
}

function basePath(target: MarketApplicationTarget) {
  return `/api/v1/admin/market/${encodeURIComponent(target)}`;
}

function itemPath(
  target: MarketApplicationTarget,
  itemType: MarketItemType,
  itemKey: string,
) {
  return `${basePath(target)}/items/${encodeURIComponent(itemType)}/${encodeURIComponent(itemKey)}`;
}

export function createMarketApi(api: MarketHttpClient) {
  return {
    listItems: async (
      target: MarketApplicationTarget,
      itemType?: MarketItemType,
      options?: MarketRequestOptions,
    ): Promise<ListMarketItemsReply> =>
      api.post(
        `${basePath(target)}/items/list`,
        { itemType: itemType ?? '' },
        options,
      ),

    listCategories: async (
      target: MarketApplicationTarget,
      params: {
        marketType: MarketType;
        itemType: MarketItemType;
        includeDisabled?: boolean;
      },
      options?: MarketRequestOptions,
    ): Promise<ListMarketCategoriesReply> =>
      api.post(`${basePath(target)}/categories/list`, params, options),

    upsertCategory: async (
      target: MarketApplicationTarget,
      params: {
        itemType: MarketItemType;
        categoryId: string;
        displayNameZh: string;
        displayNameEn: string;
        sortOrder: number;
        enabled: boolean;
        expectedLockVersion: ProtoInt64;
      },
      options?: MarketRequestOptions,
    ): Promise<{ category: MarketCategory }> =>
      api.post(
        `${basePath(target)}/categories/${encodeURIComponent(params.itemType)}/${encodeURIComponent(params.categoryId)}:upsert`,
        params,
        options,
      ),

    listReleases: async (
      target: MarketApplicationTarget,
      itemType: MarketItemType,
      itemKey: string,
      options?: MarketRequestOptions,
    ): Promise<ListMarketReleasesReply> =>
      api.post(
        `${itemPath(target, itemType, itemKey)}/releases/list`,
        {
          itemType,
          itemKey,
        },
        options,
      ),

    setActiveRelease: async (
      target: MarketApplicationTarget,
      params: {
        itemType: MarketItemType;
        itemKey: string;
        version: string;
        expectedLockVersion: ProtoInt64;
      },
      options?: MarketRequestOptions,
    ): Promise<{ item: MarketItem }> =>
      api.post(
        `${itemPath(target, params.itemType, params.itemKey)}/active-release:set`,
        params,
        options,
      ),

    setListing: async (
      target: MarketApplicationTarget,
      params: {
        itemType: MarketItemType;
        itemKey: string;
        marketType: MarketType;
        isListed: boolean;
        expectedIsListed: boolean;
        categoryId: string;
        categoryRank: number;
        featured: boolean;
        featuredRank: number;
      },
      options?: MarketRequestOptions,
    ): Promise<{ item: MarketItem }> =>
      api.post(
        `${itemPath(target, params.itemType, params.itemKey)}/listing:set`,
        params,
        options,
      ),

    revokeRelease: async (
      target: MarketApplicationTarget,
      params: {
        itemType: MarketItemType;
        itemKey: string;
        version: string;
      },
      options?: MarketRequestOptions,
    ): Promise<{ release: MarketRelease }> =>
      api.post(
        `${itemPath(target, params.itemType, params.itemKey)}/releases/${encodeURIComponent(params.version)}:revoke`,
        params,
        options,
      ),

    deleteItem: async (
      target: MarketApplicationTarget,
      params: {
        itemType: MarketItemType;
        itemKey: string;
        expectedLockVersion: ProtoInt64;
      },
      options?: MarketRequestOptions,
    ): Promise<{ itemType: MarketItemType; itemKey: string }> =>
      api.post(
        `${itemPath(target, params.itemType, params.itemKey)}:delete`,
        params,
        options,
      ),

    listAuditLogs: async (
      target: MarketApplicationTarget,
      itemType: MarketItemType,
      itemKey: string,
      limit = 100,
      options?: MarketRequestOptions,
    ): Promise<ListMarketAuditLogsReply> =>
      api.post(
        `${itemPath(target, itemType, itemKey)}/audit-logs/list`,
        {
          itemType,
          itemKey,
          limit,
        },
        options,
      ),
  };
}
